/**
 * Validation utility for ResizablePanel width percentages
 *
 * This utility ensures that width percentages follow these rules:
 * 1. minWidthPercent <= defaultWidthPercent <= maxWidthPercent
 * 2. All values must be positive numbers between 1 and 100
 * 3. All three values can be equal (e.g., all set to 50)
 */

export interface ResizablePanelWidthConfig {
  defaultWidthPercent: number;
  minWidthPercent: number;
  maxWidthPercent: number;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates resizable panel width configuration
 * @param config - The width configuration to validate
 * @returns ValidationResult with isValid flag and error messages
 */
export function validateResizablePanelWidths(
  config: ResizablePanelWidthConfig
): ValidationResult {
  const errors: string[] = [];
  const { defaultWidthPercent, minWidthPercent, maxWidthPercent } = config;

  // Check if all values are numbers
  if (typeof defaultWidthPercent !== 'number') {
    errors.push('defaultWidthPercent must be a number');
  }
  if (typeof minWidthPercent !== 'number') {
    errors.push('minWidthPercent must be a number');
  }
  if (typeof maxWidthPercent !== 'number') {
    errors.push('maxWidthPercent must be a number');
  }

  // If any value is not a number, return early
  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  // Check if values are within valid range (1-100)
  if (defaultWidthPercent < 1 || defaultWidthPercent > 100) {
    errors.push(
      `defaultWidthPercent must be between 1 and 100, got ${defaultWidthPercent}`
    );
  }
  if (minWidthPercent < 1 || minWidthPercent > 100) {
    errors.push(
      `minWidthPercent must be between 1 and 100, got ${minWidthPercent}`
    );
  }
  if (maxWidthPercent < 1 || maxWidthPercent > 100) {
    errors.push(
      `maxWidthPercent must be between 1 and 100, got ${maxWidthPercent}`
    );
  }

  // Check the ordering constraints
  if (minWidthPercent > defaultWidthPercent) {
    errors.push(
      `minWidthPercent (${minWidthPercent}) cannot be greater than defaultWidthPercent (${defaultWidthPercent})`
    );
  }

  if (defaultWidthPercent > maxWidthPercent) {
    errors.push(
      `defaultWidthPercent (${defaultWidthPercent}) cannot be greater than maxWidthPercent (${maxWidthPercent})`
    );
  }

  if (minWidthPercent > maxWidthPercent) {
    errors.push(
      `minWidthPercent (${minWidthPercent}) cannot be greater than maxWidthPercent (${maxWidthPercent})`
    );
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates and throws an error if configuration is invalid
 * Use this for compile-time validation (will fail during build if used in ngOnInit)
 * @param config - The width configuration to validate
 * @param componentName - Optional name of the component using the resizable panel
 * @throws Error if validation fails
 */
export function assertValidResizablePanelWidths(
  config: ResizablePanelWidthConfig,
  componentName?: string
): void {
  const result = validateResizablePanelWidths(config);

  if (!result.isValid) {
    const componentInfo = componentName && componentName !== 'Check the browser console for the full error location'
      ? `Component: ${componentName} | `
      : '';

    const errorMessage = `❌ RESIZABLE PANEL VALIDATION ERROR
${componentInfo}Invalid Configuration: ${result.errors.join(', ')}
Current: min=${config.minWidthPercent}, default=${config.defaultWidthPercent}, max=${config.maxWidthPercent}
Required: minWidthPercent <= defaultWidthPercent <= maxWidthPercent (all between 1-100)
Fix: Find <app-resizable-panel> and update [minWidthPercent], [defaultWidthPercent], [maxWidthPercent]`;

    throw new Error(errorMessage);
  }
}

/**
 * Type guard to ensure width configuration is valid
 * This provides compile-time safety when using strict configurations
 */
export function isValidWidthConfig(
  config: ResizablePanelWidthConfig
): config is ResizablePanelWidthConfig {
  return validateResizablePanelWidths(config).isValid;
}

/**
 * Helper function to create a validated width configuration
 * This ensures the configuration is valid before it's used
 * @param config - The width configuration
 * @returns The validated configuration
 * @throws Error if validation fails
 */
export function createValidatedWidthConfig(
  config: ResizablePanelWidthConfig
): ResizablePanelWidthConfig {
  assertValidResizablePanelWidths(config);
  return config;
}
